import './Annuaire.css';
import '../../composants/SuspensionModal/SuspensionModal.css'; // FRONT-19: Badge suspendu
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import TabNav from '../../composants/TabNav/TabNav';
import TableauDeBord from '../../composants/TableauDeBord/TableauDeBord';
import { ReactComponent as DocumentsIcon } from "../../assets/icons/document.svg";
import { useAuth } from '../../contexts/AuthContext';
import { isClientRole, isPrestataireRole, isAdminRole } from '../../utils/roleUtils';
import { apiService } from '../../services/apiService';
import Button from '../../composants/Button/Button';
import DocumentsList from '../../composants/DocumentsList/DocumentsList';
import { FlagCell } from '../../composants/FlagCell/FlagCell';
import logoFallback from '../../assets/logo/logotype-centralis-orange.svg';
import { ReactComponent as ArrowDownIcon } from '../../assets/icons/arrow-down.svg';
import { ReactComponent as ArrowUpIcon } from '../../assets/icons/arrow-up.svg';
import ModalProfilMembre from '../Publications/components/DetailCandidature/ModalProfilMembre';
import AcheteurModal, { DEPT_NAMES, formatDepartement } from '../../composants/AcheteurModal/AcheteurModal';
import { departementFromCodePostal } from '../../utils/departement';
import type { ProfileData } from '../../services/profileService';

// Interfaces pour les données de l'API
interface Entite {
  id: number;
  nom: string;
  type: string;
  logo_url?: string | null;
  suspendu?: boolean; // FRONT-19: Statut de suspension
}

interface LotTechnique {
  id: number;
  nom: string;
  code: string;
  image_url?: string | null;
}

interface Prestataire {
  id: number;
  nom: string;
  prenom: string;
  email: string;
  telephone: string;
  fonction: string;
}

interface Departement {
  id: number;
  nom: string;
}

interface Contact {
  id: number | string;  // FRONT-13: Peut être composite (ex: "123_lot_100")
  rang: number;
  numero: string;
  entite: Entite;
  lot_technique: LotTechnique;
  prestataire: Prestataire;
  departements: Departement[];
  // Autres champs potentiellement ajoutés pour la vue
  commandesCount?: number;
  commandesValue?: number;
  documents?: {
    id: number;
    type: string;
    category?: 'accord_cadre' | 'entreprise' | 'commun';  // FRONT-13: Catégorie du document
    key?: string;
    commentaire?: string;
    filename?: string;
    updated_at: string;
  }[];
}

interface ApiResponse {
  status: string;
  data: Contact[];
  pagination: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
  };
}

// Modification de l'interface FilterOption pour permettre les valeurs numériques
interface FilterOption {
  label: string;
  value: string | number;
}

// Ajout des interfaces pour les nouvelles structures de données API
interface LotTechniqueApi {
  id: number;
  nom: string;
  code: string;
}

interface DepartementApi {
  id: number;
  id_region: number;
  nom: string;
  created_at: string;
  updated_at: string;
}

interface LotDepartementApi {
  id: number;
  id_departement: number;
  created_at: string;
  updated_at: string;
  departement: DepartementApi;
  contrats: any[]; // Structure simplifiée pour ce cas d'usage
}

interface LotDepartementsResponse {
  [key: string]: LotDepartementApi;
}

function getInitialsAcheteur(nom: string): string {
  return nom.split(/\s+/).slice(0, 2).map(w => w[0]?.toUpperCase() || '').join('');
}

function AcheteurCardSkeleton() {
  return (
    <div className="acheteur-card acheteur-card-skeleton">
      <span className="acheteur-badge-dot sk-badge-dot"></span>
      <div className="acheteur-card-avatar sk-avatar"></div>
      <div className="acheteur-card-body">
        <span className="sk-ac sk-ac-nom"></span>
        <span className="sk-ac sk-ac-dept"></span>
        <span className="sk-ac sk-ac-cat"></span>
      </div>
    </div>
  );
}

function PrestataireCardSkeleton() {
  return (
    <div className="prestataire-card prestataire-card-skeleton prestataire-card--readonly">
      <div className="prestataire-card-inner">
        <span className="sk sk-badge" />
        <div className="prestataire-card-header">
          <div className="sk sk-banner" />
        </div>
        <div className="prestataire-card-logo-wrapper">
          <div className="sk sk-logo" />
        </div>
        <div className="prestataire-card-body">
          <div className="sk sk-line sk-line--nom" />
          <div className="sk sk-line sk-line--lot" />
          <div className="sk sk-stars-bar" />
        </div>
      </div>
    </div>
  );
}

function StarRating({ rating, count = 0 }: { rating: number; count?: number }) {
  return (
    <div className="prestataire-card-stars">
      <span className="stars-wrap" aria-label={`${rating} sur 5`}>
        {[1, 2, 3, 4, 5].map(i => {
          const type = rating >= i ? 'full' : rating >= i - 0.5 ? 'half' : 'empty';
          return (
            <span key={i} className={`star star--${type}`} aria-hidden="true">
              {type === 'half' ? (
                <><span className="star-half">★</span><span className="star-bg">★</span></>
              ) : type === 'full' ? '★' : '☆'}
            </span>
          );
        })}
      </span>
      <span className="prestataire-card-avis">({count} avis)</span>
    </div>
  );
}

interface AnnuaireProps {
  defaultTab?: 'prestataires' | 'acheteurs';
  defaultRang?: string;
  hideTitle?: boolean;
  hideTabs?: boolean;
}

const Annuaire: React.FC<AnnuaireProps> = ({ defaultTab, defaultRang, hideTitle, hideTabs }) => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [activeTabName, setActiveTabName] = useState<'prestataires' | 'acheteurs'>(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam === 'acheteurs') return 'acheteurs';
    if (tabParam === 'prestataires') return 'prestataires';
    if (defaultTab) return defaultTab;
    return 'prestataires';
  });
  const [contacts, setContacts] = useState<Contact[]>([]);
  // Moyenne + nombre d'avis par entite (carte annuaire), charge en bulk.
  const [avisParEntite, setAvisParEntite] = useState<Record<number, { nombre: number; moyenne: number | null }>>({});
  const [acheteurs, setAcheteurs] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const [modalDocuments, setModalDocuments] = useState<Contact['documents']>([]);
  const [modalTitle, setModalTitle] = useState<string>('');
  // Nouvel état pour stocker les données des API
  const [lotsTechniques, setLotsTechniques] = useState<LotTechniqueApi[]>([]);
  const [lotsDepartements, setLotsDepartements] = useState<LotDepartementApi[]>([]);
  const [openDocumentModalId, setOpenDocumentModalId] = useState<number | string | null>(null);  // Peut être string (ID composite)
  const [annuaireProfile, setAnnuaireProfile] = useState<ProfileData | null>(null);
  const [annuaireProfileLoading, setAnnuaireProfileLoading] = useState(false);
  const [annuaireProfileError, setAnnuaireProfileError] = useState<string | null>(null);
  const [showAnnuaireProfileModal, setShowAnnuaireProfileModal] = useState(false);
  const [annuaireTypeBadge, setAnnuaireTypeBadge] = useState<string | null>(null);
  const [annuaireContactInfo, setAnnuaireContactInfo] = useState<{ nom: string; prenom: string; email: string; telephone: string; fonction: string; contratNumero?: string | null } | null>(null);
  const [annuaireContactDocuments, setAnnuaireContactDocuments] = useState<Contact['documents']>(undefined);
  const [commandeInitialValues, setCommandeInitialValues] = useState<{ lotTechnique: string; lotGeographique: string }>({ lotTechnique: '', lotGeographique: '' });

  const formatPhone = (phone: string): string => {
    const digits = phone.replace(/\D/g, '');
    let national = digits;
    if (digits.startsWith('33') && digits.length === 11) {
      national = digits.slice(2);
    } else if (digits.startsWith('0') && digits.length === 10) {
      national = digits.slice(1);
    } else {
      return phone;
    }
    return `+33 ${national[0]} ${national.slice(1, 3)} ${national.slice(3, 5)} ${national.slice(5, 7)} ${national.slice(7, 9)}`;
  };


  const [paginationContacts, setPaginationContacts] = useState({
    currentPage: 1,
    lastPage: 1,
    perPage: 20,
    total: 0
  });

  const [paginationAcheteurs, setPaginationAcheteurs] = useState({
    currentPage: 1,
    lastPage: 1,
    perPage: 20,
    total: 0
  });

  // Acheteurs tab: card/table toggle, filters, modal
  const [acheteurFilterNom, setAcheteurFilterNom] = useState('');
  const [acheteurFilterDept, setAcheteurFilterDept] = useState('');
  const [acheteurFilterCategorie, setAcheteurFilterCategorie] = useState('');
  const [acheteurFilterType, setAcheteurFilterType] = useState<'' | 'membre' | 'standard'>('');
  const [selectedAcheteur, setSelectedAcheteur] = useState<any | null>(null);
  const [showAcheteurModal, setShowAcheteurModal] = useState(false);

  // États pour les filtres
  const [departementOptions, setDepartementOptions] = useState<FilterOption[]>([]);
  const [lotOptions, setLotOptions] = useState<FilterOption[]>([{ label: "Tous les lots", value: "" }]);
  const [rangOptions, setRangOptions] = useState<FilterOption[]>([{ label: "Tous", value: "" }]);
  const [nomOptions, setNomOptions] = useState<FilterOption[]>([{ label: "Toutes les entreprises", value: "" }]);

  const [selectedDepartement, setSelectedDepartement] = useState<string | number>(75);
  const [selectedLot, setSelectedLot] = useState<string | number>("");
  const [selectedRang, setSelectedRang] = useState<string>(defaultRang ?? "");
  const [selectedNom, setSelectedNom] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState<string>("");

  // État pour stocker tous les contacts (non filtrés)
  const [allContacts, setAllContacts] = useState<Contact[]>([]);

  // Utilisation de roleUtils pour déterminer le rôle de l'utilisateur
  const isClient = isClientRole(user?.role || '');
  const isPrestataire = isPrestataireRole(user?.role || '');
  const isAdmin = isAdminRole(user?.role || '');

  // Seuls les acheteurs connectés peuvent ouvrir le profil d'un prestataire
  const canOpenModal = Boolean(user) && isClient;

  // Incitation à l'inscription pour les non-connectés
  const [showSignupPrompt, setShowSignupPrompt] = useState(false);

  // ← false = vue tableau, true = vue cartes
  const CARD_VIEW = true;

  // Acheteurs tab: filtered list (client-side)
  const filteredAcheteurs = useMemo(() => {
    let list = [...acheteurs];
    if (acheteurFilterNom.trim()) {
      const q = acheteurFilterNom.toLowerCase();
      list = list.filter(a => (a.nom || '').toLowerCase().includes(q));
    }
    if (acheteurFilterDept) {
      list = list.filter(a => a.departement_code === acheteurFilterDept);
    }
    if (acheteurFilterCategorie) {
      if (acheteurFilterCategorie === '__null') {
        list = list.filter(a => !a.type_organisme);
      } else {
        list = list.filter(a => a.type_organisme === acheteurFilterCategorie);
      }
    }
    if (acheteurFilterType === 'membre') {
      list = list.filter(a => a.has_recent_orders);
    } else if (acheteurFilterType === 'standard') {
      list = list.filter(a => !a.has_recent_orders);
    }
    return list;
  }, [acheteurs, acheteurFilterNom, acheteurFilterDept, acheteurFilterCategorie, acheteurFilterType]);

  const acheteurCategorieOptions = useMemo(() => {
    const seen = new Set<string>();
    const opts: Array<{ val: string; label: string }> = [];
    acheteurs.forEach(a => {
      const key = a.type_organisme || '__null';
      if (!seen.has(key)) {
        seen.add(key);
        opts.push({ val: key, label: a.type_organisme || 'Non renseigné' });
      }
    });
    return opts.sort((a, b) => a.label.localeCompare(b.label, 'fr'));
  }, [acheteurs]);

  const acheteurDeptOptions = useMemo(() => {
    const seen = new Set<string>();
    acheteurs.forEach(a => { if (a.departement_code) seen.add(a.departement_code); });
    return Array.from(seen).sort((a, b) => a.localeCompare(b, 'fr', { numeric: true }));
  }, [acheteurs]);

  useEffect(() => {
    if (openDocumentModalId !== null) {
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden'; // ajouter html
    } else {
      document.body.style.overflow = 'auto';
      document.documentElement.style.overflow = 'auto';
    }

    return () => {
      document.body.style.overflow = 'auto';
      document.documentElement.style.overflow = 'auto';
    };
  }, [openDocumentModalId]);

  // Charge en bulk la moyenne + le nombre d'avis des entites affichees (cartes annuaire).
  useEffect(() => {
    const ids = Array.from(new Set(contacts.map((c) => c.entite?.id).filter((v): v is number => typeof v === 'number')));
    if (!ids.length) return;
    let cancelled = false;
    apiService.getAvisPrestataireMoyennes(ids)
      .then((map) => { if (!cancelled) setAvisParEntite((prev) => ({ ...prev, ...map })); })
      .catch(() => undefined);
    return () => { cancelled = true; };
  }, [contacts]);

  useEffect(() => {
    if (showAnnuaireProfileModal) {
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
      document.documentElement.style.overflow = 'auto';
    }

    return () => {
      document.body.style.overflow = 'auto';
      document.documentElement.style.overflow = 'auto';
    };
  }, [showAnnuaireProfileModal]);

  const openProfileModal = useCallback((entiteId: number, badge?: string | null, contactInfo?: { nom: string; prenom: string; email: string; telephone: string; fonction: string; contratNumero?: string | null } | null, documents?: Contact['documents']) => {
    setAnnuaireProfile(null);
    setAnnuaireProfileError(null);
    setAnnuaireProfileLoading(true);
    setShowAnnuaireProfileModal(true);
    setAnnuaireTypeBadge(badge ?? null);
    setAnnuaireContactInfo(contactInfo ?? null);
    setAnnuaireContactDocuments(documents ?? undefined);

    const token = localStorage.getItem('token');
    fetch(
      `${process.env.REACT_APP_API_URL}/annuaire/entites/${entiteId}/profil`,
      { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } },
    )
      .then((r) => {
        if (!r.ok) throw new Error(`Erreur ${r.status}`);
        return r.json();
      })
      .then((d) => setAnnuaireProfile(d?.data ?? d))
      .catch((err: Error) => setAnnuaireProfileError(err.message || 'Impossible de charger le profil.'))
      .finally(() => setAnnuaireProfileLoading(false));
  }, []);

  // Définir les onglets en fonction du rôle
  const tabs = isClient
    ? ["Prestataires", "Acheteurs"]
    : ["Acheteurs", "Prestataires"];

  // Index numérique dérivé du nom d'onglet (pour TabNav)
  const activeTab = isClient
    ? (activeTabName === 'prestataires' ? 0 : 1)
    : (activeTabName === 'acheteurs' ? 0 : 1);

  // Fonction pour déterminer si l'onglet actuel est "Annuaire des acheteurs"
  const isAcheteurTab = () => activeTabName === 'acheteurs';


  const getColumns = () => {
    const baseColumns = [
      {
        field: 'lot_technique',
        header: 'Catégories',
        renderCell: (_: any, row: Contact) => (
          <div
            className="categorie-cell"
            style={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: '180px'
            }}
            title={`${row.lot_technique?.code} - ${row.lot_technique?.nom}` || '-'}
          >
            {`${row.lot_technique?.code} - ${row.lot_technique?.nom}` || '-'}
          </div>
        )
      },
      {
        field: 'departements',
        header: 'Départements',
        renderCell: (_: any, row: Contact) => {
          // Si aucun département n'est sélectionné, afficher tous les départements comme avant
          if (!selectedDepartement) {
            return (
              <div className="departement-cell">
                {row.departements && row.departements.length > 0
                  ? row.departements.map(dep => dep.nom).join(', ')
                  : '-'}
              </div>
            );
          }

          // Sinon, chercher le département sélectionné dans la liste des départements du contact
          const selectedDep = row.departements?.find(dep => dep.id === Number(selectedDepartement));

          return (
            <div className="departement-cell">
              {selectedDep ? `${selectedDep.id} - ${selectedDep.nom}` : '-'}
            </div>
          );
        }
      },
      {
        field: 'rang',
        header: 'Type',
        renderCell: (_: any, row: Contact) => (
          <div className="rang-cell" style={{ whiteSpace: 'nowrap', textAlign: 'left', paddingLeft: '16px', minWidth: '160px' }}>
            {row.rang === 0 ? 'Libre' : `Titulaire rang ${row.rang}`}
          </div>
        )
      },
      {
        field: 'logo',
        header: 'Logo',
        renderCell: (_: any, row: Contact) => {
          // Si pas de logo_url, ne rien afficher
          if (!row.entite?.logo_url) {
            return <div style={{ width: 40, height: 40 }}></div>;
          }

          return (
            <div style={{ width: '100%', display: 'flex', justifyContent: 'flex-end', paddingRight: '8px' }}>
              <img
                src={row.entite.logo_url}
                alt={`${row.entite.nom} logo`}
                className="entite-logo"
                style={{ width: 40, height: 40, objectFit: 'contain' }}
                onError={(e) => {
                  // En cas d'erreur, cacher l'image
                  e.currentTarget.style.display = 'none';
                }}
              />
            </div>
          );
        }
      },
      {
        field: 'entite',
        header: 'Entreprise',
        renderCell: (_: any, row: Contact) => (
          <div
            className="entreprise-cell"
            style={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: '280px',
              minWidth: '200px'
            }}
            title={row.entite?.nom.toUpperCase() || '-'}
          >
            {row.entite?.nom.toUpperCase() || '-'}
          </div>
        )
      },
      ...(!isPrestataire && user ? [{
        field: 'prestataire',
        header: 'Information',
        renderCell: (_: any, row: Contact) => (
          <div className="contact-info-cell" style={{ maxWidth: '200px' }}>
            <div className="contact-name" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {row.prestataire ? `${row.prestataire.prenom} ${row.prestataire.nom}` : '-'}
            </div>
            <div className="contact-email" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {row.prestataire?.email || '-'}
            </div>
            <div className="contact-phone" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {row.prestataire?.telephone || '-'}
            </div>
          </div>
        )
      },
      // FRONT-19: Colonne Statut (badge suspendu)
      {
        field: 'statut',
        header: 'Statut',
        renderCell: (_: any, row: Contact) => (
          row.entite?.suspendu
            ? <span className="annuaire-badge-suspendu">Suspendu</span>
            : null
        )
      },
      {
        field: 'documents',
        header: 'Documents',
        renderCell: (_: any, row: Contact) => {
          if (!user) return <span className="no-documents">Vous devez vous connecter</span>;

          // Les documents sont maintenant directement inclus dans row.documents par le backend
          // Les documents communs (CCTP) sont inclus uniquement pour les titulaires (pas les PL)
          const allDocuments = row.documents || [];

          // Afficher "Aucun document" uniquement si vraiment aucun document
          if (!allDocuments.length) {
            return <Button className="no-documents documents-button" value="Aucun document" variant='secondary' size='small' disabled />;
          }

          return (
            <Button
              onClick={() => {
                setModalDocuments(allDocuments);
                setModalTitle(`Documents de ${row.entite?.nom || 'ce contact'}`);
                setOpenDocumentModalId(row.id);
              }}
              className="documents-button"
              style={{ padding: '1rem 0.5rem', paddingInline: '0.5rem', paddingBlock: '1rem' }}
              size='small'
              value={`Voir les documents (${allDocuments.length})`}
            />


          );
        }
      }
        // {
        //   field: 'Evaluation',
        //   header: 'Évaluation',
        //   renderCell: (_: any, row: Contact) => (
        //     <div className="contact-info-cell">
        //     </div>
        //   )
        // },
      ] : []),
    ];

    return baseColumns;
  };

  const handleSearch = (query: string) => {
    setSearchQuery(query);
  };

  const handleExport = () => {
  };

  const handleFilter = () => {
  };

  // Fonction pour filtrer les contacts en front-end
  const filterContactsLocally = () => {
    let filtered = [...allContacts];

    // Filtrer par recherche
    if (searchQuery.trim()) {
      const searchTerms = searchQuery.toLowerCase().split(' ').filter(term => term.length > 0);
      filtered = filtered.filter(contact => {
        const searchableText = `
        ${contact.entite?.nom || ''}
        ${contact.prestataire?.nom || ''}
        ${contact.prestataire?.prenom || ''}
        ${contact.prestataire?.email || ''}
        ${contact.lot_technique?.nom || ''}
        ${contact.lot_technique?.code || ''}
      `.toLowerCase();

        return searchTerms.every(term => searchableText.includes(term));
      });
    }

    // ❌ PAS de filtre par département ici (fait côté backend maintenant)

    // Filtrer par lot technique (utiliser CODE pas ID)
    if (selectedLot !== "") {
      const lotCode = String(selectedLot);

      filtered = filtered.filter(contact => {
        const contactLotCode = String(contact.lot_technique?.code || '');
        const match = contactLotCode === lotCode;
        return match;
      });
    }

    // Filtrer par rang
    if (selectedRang !== "") {
      const rangValue = Number(selectedRang);

      filtered = filtered.filter(contact => {
        const match = contact.rang === rangValue;
        return match;
      });
    }

    // Filtrer par nom d'entreprise
    if (selectedNom.trim()) {
      const nomSearch = selectedNom.toLowerCase().trim();
      filtered = filtered.filter(contact =>
        contact.entite?.nom?.toLowerCase().includes(nomSearch)
      );
    }

    // Trier les résultats : catégorie → lot → rang (1,2,3 puis référencé) → département
    const sortedFiltered = filtered.sort((a, b) => {
      // 1. Tri par catégorie (centaine du lot_technique.code)
      const lotCodeA = Number(a.lot_technique?.code) || 0;
      const lotCodeB = Number(b.lot_technique?.code) || 0;

      const categoryA = Math.floor(lotCodeA / 100);
      const categoryB = Math.floor(lotCodeB / 100);

      if (categoryA !== categoryB) {
        return categoryA - categoryB;
      }

      // 2. Tri par lot technique (code)
      if (lotCodeA !== lotCodeB) {
        return lotCodeA - lotCodeB;
      }

      // 3. Tri par rang (Type) : rang 1, 2, 3, puis référencé (0)
      const rangA = a.rang ?? 999; // Utiliser 999 pour les valeurs null/undefined
      const rangB = b.rang ?? 999;

      // Les référencés (rang 0) viennent en dernier, avant les valeurs nulles
      if (rangA === 0) {
        if (rangB === 0 || rangB === 999) {
          // Si les deux sont référencés ou null, continuer au tri suivant
          if (rangB === 999) return -1; // 0 avant null
          // Les deux sont 0, continuer
        } else {
          return 1; // 0 après les rangs 1,2,3
        }
      } else if (rangB === 0) {
        return -1; // Les rangs 1,2,3 avant 0
      }

      // Sinon tri croissant des rangs (1 avant 2, 2 avant 3)
      if (rangA !== rangB) {
        return rangA - rangB;
      }

      // 4. Tri par département
      // Si un département est sélectionné, utiliser celui-là pour le tri
      // Sinon, utiliser le premier département de la liste
      let depIdA = 999;
      let depIdB = 999;

      if (selectedDepartement !== "") {
        // Utiliser le département sélectionné s'il existe dans la liste
        const selectedDepId = Number(selectedDepartement);
        depIdA = a.departements?.some(d => d.id === selectedDepId) ? selectedDepId : 999;
        depIdB = b.departements?.some(d => d.id === selectedDepId) ? selectedDepId : 999;
      } else {
        // Sinon utiliser le premier département
        depIdA = a.departements?.[0]?.id || 999;
        depIdB = b.departements?.[0]?.id || 999;
      }

      if (depIdA !== depIdB) {
        return depIdA - depIdB;
      }

      // 5. En dernier lieu, tri alphabétique par nom d'entreprise
      const nomA = a.entite?.nom?.toLowerCase() || '';
      const nomB = b.entite?.nom?.toLowerCase() || '';

      return nomA.localeCompare(nomB);
    });

    setContacts(sortedFiltered);
  };

  // Fonctions de gestion des changements de filtres
  const handleDepartementChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    const departementValue = value === "" ? "" : Number(value);
    setSelectedDepartement(departementValue);
    // Pas d'appel API, juste filtrage local
  };

  const handleLotChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    const lotValue = value === "" ? "" : Number(value);
    setSelectedLot(lotValue);
    // Pas d'appel API, juste filtrage local
  };

  const handleRangChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setSelectedRang(value);
    // Pas d'appel API, juste filtrage local
  };

  const handleNomChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSelectedNom(value);
    // Pas d'appel API, juste filtrage local
  };

  // Nouvelle fonction pour récupérer les lots techniques depuis l'API
  const fetchLotsTechniques = async () => {
    try {
      // Récupérer le token d'authentification
      const token = localStorage.getItem('token');

      // if (!token) {
      //   throw new Error("Utilisateur non authentifié");
      // }

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/lots-technique/categories`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Erreur HTTP ${response.status}`);
      }

      const result = await response.json();

      // Déterminer si nous avons un tableau ou un objet avec une propriété data
      const lotsData = Array.isArray(result) ? result :
        (result.data && Array.isArray(result.data)) ? result.data :
          [];

      setLotsTechniques(lotsData);

      // ✅ Utiliser le CODE comme value (pas l'ID)
      const options: FilterOption[] = lotsData.map((lot: LotTechniqueApi) => ({
        label: `${lot.code} - ${lot.nom}`,
        value: lot.code // ✅ Utiliser le CODE qui correspond aux données des contacts
      }));

      // Trier par code numérique
      options.sort((a, b) => Number(a.value) - Number(b.value));

      // Ajouter l'option "Tous"
      options.unshift({ label: "Tous les lots", value: "" });

      setLotOptions(options);

    } catch (error: any) {
      console.error('Erreur lors du chargement des lots techniques:', error);
      setError(`Impossible de charger les lots techniques: ${error.message}`);
    }
  };

  const compareDepartements = (a: FilterOption, b: FilterOption) => {
    // Extraire les numéros de département du label (format: "XX - Nom département")
    const numA = parseInt(a.label.split(' - ')[0]);
    const numB = parseInt(b.label.split(' - ')[0]);

    // Gérer les cas spéciaux (2A, 2B, etc.)
    if (isNaN(numA) || isNaN(numB)) {
      return a.label.localeCompare(b.label);
    }

    return numA - numB;
  };

  // Nouvelle fonction pour récupérer les départements depuis l'API
  const fetchLotsDepartements = async () => {
    try {
      // Récupérer le token d'authentification
      const token = localStorage.getItem('token');

      // if (!token) {
      //   throw new Error("Utilisateur non authentifié");
      // }

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/departements`, // URL corrigée
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Erreur HTTP ${response.status}`);
      }

      const result = await response.json();

      // Extraire les données de départements selon la structure de la réponse
      const departementsData = result.data || [];

      // Vérifier si nous avons bien un tableau de départements
      if (Array.isArray(departementsData)) {
        // Créer les options pour le dropdown des départements
        const options = departementsData.map(dep => ({
          label: `${dep.id} - ${dep.nom}`,
          value: dep.id
        }))
          .sort(compareDepartements); // Utiliser notre fonction de tri personnalisée

        setDepartementOptions(options);
      } else {
        console.error('Format de données de départements inattendu:', departementsData);
        throw new Error('Format de données de départements inattendu');
      }
    } catch (error: any) {
      console.error('Erreur lors du chargement des départements:', error);
      setError(`Impossible de charger les départements: ${error.message}`);
    }
  };

  // Extraire les rangs et noms d'entreprises à partir des données de contacts
  const extractRangAndNomOptions = (data: Contact[]) => {
    // Créer des Sets pour éviter les doublons
    const rangs = new Set<string>();
    const noms = new Set<string>();

    data.forEach(contact => {
      // Extraire les rangs
      if (contact.rang) {
        rangs.add(contact.rang.toString());
      }

      // Extraire les noms d'entreprise
      if (contact.entite?.nom) {
        noms.add(contact.entite.nom);
      }
    });

    // Définir les rangs possibles avec leurs libellés explicites
    const rangOptions = [
      { label: "Tous", value: "" },
      { label: "Titulaire de rang 1", value: "1" },
      { label: "Titulaire de rang 2", value: "2" },
      { label: "Titulaire de rang 3", value: "3" },
      { label: "Libre", value: "0" }
    ];

    const nomOptions = Array.from(noms)
      .sort()
      .map(nom => ({ label: nom, value: nom }));

    // Ajouter l'option "Tous" au début de la liste des noms
    nomOptions.unshift({ label: "Toutes les entreprises", value: "" });

    return { rangOptions, nomOptions };
  };

  useEffect(() => {
    if (allContacts.length > 0) {
      filterContactsLocally();
    }
  }, [searchQuery, selectedLot, selectedRang, selectedNom, allContacts]); // ❌ Retrait de selectedDepartement (géré côté backend)

  // Fonction pour récupérer les contacts depuis l'API
  // const fetchContacts = async (
  //   page: number = 1,
  //   perPage: number = paginationContacts.perPage,
  //   search: string = "",
  //   departement: string | number = "",
  //   lot: string | number = "",
  //   rang: string = "",
  //   nom: string = ""
  // ) => {
  //   try {
  //     setLoading(true);

  //     const queryParams = new URLSearchParams();
  //     queryParams.append('page', page.toString());
  //     queryParams.append('per_page', perPage.toString());

  //     if (search) queryParams.append('search', search);
  //     if (departement !== "") queryParams.append('departement', departement.toString());
  //     if (lot !== "") queryParams.append('lot', lot.toString());
  //     if (rang) queryParams.append('rang', rang);
  //     if (nom) queryParams.append('nom', nom);

  //     const token = localStorage.getItem('token');

  //     const response = await fetch(
  //       `${process.env.REACT_APP_API_URL}/prestataire/annuaire?${queryParams.toString()}`,
  //       {
  //         headers: {
  //           'Authorization': `Bearer ${token}`,
  //           'Content-Type': 'application/json'
  //         }
  //       }
  //     );

  //     if (!response.ok) throw new Error(`Erreur HTTP ${response.status}`);

  //     const result: ApiResponse = await response.json();
  //     console.log("Structure des contacts:", result);
  //     console.log("Last.page:", result.pagination.last_page);
  //     setContacts([...result.data]);

  //     setPaginationContacts({
  //       currentPage: result.pagination.current_page,
  //       lastPage: result.pagination.last_page,
  //       perPage: result.pagination.per_page,
  //       total: result.pagination.total
  //     });

  //     const { rangOptions: newRangOptions, nomOptions: newNomOptions } = extractRangAndNomOptions(result.data);
  //     setRangOptions(newRangOptions);
  //     setNomOptions(newNomOptions);

  //     const uniqueEntiteIds = Array.from(
  //       new Set(result.data.map(c => c.entite?.id).filter(Boolean))
  //     ) as number[];

  //     uniqueEntiteIds.forEach(id => {
  //       fetchLogoWithSessionCache(id).then(() => {
  //         // chaque logo met à jour logos au fur et à mesure via setLogos
  //       });
  //     });
  //   } catch (error: any) {
  //     console.error('Erreur lors du chargement des contacts:', error);
  //     setError(error.message);
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  const fetchContacts = async () => {
    try {
      setLoading(true);

      const token = localStorage.getItem('token');

      // ✅ Construire l'URL avec le département sélectionné si présent
      const queryParams = new URLSearchParams();
      if (selectedDepartement !== "") {
        queryParams.append('departement', selectedDepartement.toString());
      }

      const url = `${process.env.REACT_APP_API_URL}/prestataire/annuaire${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;

      // Charger les contacts avec le filtre département si applicable
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error(`Erreur HTTP ${response.status}`);

      const result: ApiResponse = await response.json();

      // Dédupliquer les données par ID
      const uniqueData = Array.from(
        new Map(result.data.map(item => [item.id, item])).values()
      );

      // Pré-trier pour éviter le flash visuel (même logique que filterContactsLocally)
      const preSorted = [...uniqueData].sort((a: Contact, b: Contact) => {
        const lotCodeA = Number(a.lot_technique?.code) || 0;
        const lotCodeB = Number(b.lot_technique?.code) || 0;
        const categoryA = Math.floor(lotCodeA / 100);
        const categoryB = Math.floor(lotCodeB / 100);
        if (categoryA !== categoryB) return categoryA - categoryB;
        if (lotCodeA !== lotCodeB) return lotCodeA - lotCodeB;
        const rangA = a.rang ?? 999;
        const rangB = b.rang ?? 999;
        if (rangA === 0 && rangB !== 0) return 1;
        if (rangB === 0 && rangA !== 0) return -1;
        if (rangA !== rangB) return rangA - rangB;
        const nomA = a.entite?.nom?.toLowerCase() || '';
        const nomB = b.entite?.nom?.toLowerCase() || '';
        return nomA.localeCompare(nomB);
      });

      // Stocker TOUS les contacts
      setAllContacts(preSorted);
      setContacts(preSorted);

      // Extraire les options de filtres
      const { rangOptions: newRangOptions, nomOptions: newNomOptions } = extractRangAndNomOptions(uniqueData);
      setRangOptions(newRangOptions);
      setNomOptions(newNomOptions);

    } catch (error: any) {
      console.error('Erreur lors du chargement des contacts:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Fonction pour récupérer les données des acheteurs
  // const fetchAcheteurs = async (
  //   page: number = 1,
  //   perPage: number = paginationAcheteurs.perPage
  // ) => {
  //   try {
  //     setLoading(true);
  //     const token = localStorage.getItem('token');

  //     const queryParams = new URLSearchParams();
  //     queryParams.append('page', page.toString());
  //     queryParams.append('per_page', perPage.toString()); // <-- nouveau

  //     const response = await fetch(
  //       `${process.env.REACT_APP_API_URL}/acheteur/annuaire?${queryParams.toString()}`,
  //       {
  //         headers: {
  //           'Authorization': `Bearer ${token}`,
  //           'Content-Type': 'application/json'
  //         }
  //       }
  //     );

  //     if (!response.ok) throw new Error(`Erreur HTTP ${response.status}`);

  //     const result = await response.json();

  //     // Tri du plus récent au plus ancien selon la date de création
  //     const sortedAcheteurs = (result.data || []).sort((a: any, b: any) => {
  //       const dateA = new Date(a.created_at).getTime();
  //       const dateB = new Date(b.created_at).getTime();
  //       return dateB - dateA; // du plus récent au plus ancien
  //     });

  //     setAcheteurs(sortedAcheteurs);

  //     console.log("Structure des acheteurs:", result);
  //     console.log("Last.page acheteurs:", result.pagination.last_page);

  //     setPaginationAcheteurs({
  //       currentPage: result.pagination.current_page,
  //       lastPage: result.pagination.last_page,
  //       perPage: result.pagination.per_page,
  //       total: result.pagination.total
  //     });
  //   } catch (error: any) {
  //     console.error('Erreur lors du chargement des acheteurs:', error);
  //     setError(error.message);
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  const fetchAcheteurs = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/acheteur/annuaire`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) throw new Error(`Erreur HTTP ${response.status}`);

      const result = await response.json();

      // Tri du plus récent au plus ancien selon la date de création
      const sortedAcheteurs = (result.data || []).sort((a: any, b: any) => {
        const dateA = new Date(a.created_at).getTime();
        const dateB = new Date(b.created_at).getTime();
        return dateB - dateA;
      });

      // ✅ Dédupliquer les acheteurs par ID
      const uniqueAcheteurs = Array.from(
        new Map(sortedAcheteurs.map((item: any) => [item.id, item])).values()
      );

      setAcheteurs(uniqueAcheteurs);
    } catch (error: any) {
      console.error('Erreur lors du chargement des acheteurs:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Fonction pour gérer le changement d'onglet
  const handleTabChange = (index: number) => {
    if (isClient) {
      setActiveTabName(index === 0 ? 'prestataires' : 'acheteurs');
    } else {
      setActiveTabName(index === 0 ? 'acheteurs' : 'prestataires');
    }
  };

  useEffect(() => {
    fetchLotsDepartements();
    if (activeTabName === 'acheteurs') {
      fetchAcheteurs();
    } else {
      fetchLotsTechniques();
      fetchContacts(); // Recharger les contacts avec le département sélectionné
    }
  }, [activeTabName, user, selectedDepartement]);

  const getDepartementLabel = (id: number | string | undefined) => {
    if (!id) return;

    let normalizedId = String(id);

    // Retirer le 0 de tête pour la recherche
    const searchId = normalizedId.startsWith("0")
      ? normalizedId.slice(1)
      : normalizedId;

    // Trouver le département correspondant
    const dep = departementOptions.find(
      (d) => String(d.value) === searchId
    );

    // Si trouvé → reformater l’affichage avec le zéro (si besoin)
    if (dep) {
      const displayCode = normalizedId.padStart(2, "0");
      const [, nom] = dep.label.split(" - ");
      return `${displayCode} - ${nom}`;
    }

    return;
  };

  // Fonction pour gérer le changement de page
  const handlePageChange = (page: number) => {
    if (isAcheteurTab()) {
      fetchAcheteurs(); // <-- utiliser la page demandée
    } else {
    }
  };

  // Rendu des filtres dropdown
  const renderFilterDropdowns = () => (
    <div className="annuaire-filters">

      <div className="filter-dropdown">
        <label htmlFor="lot-filter">Catégorie</label>
        <select
          id="lot-filter"
          value={selectedLot}
          onChange={handleLotChange}
        >
          {lotOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      <div className="filter-dropdown">
        <label htmlFor="departement-filter">Département</label>
        <select
          id="departement-filter"
          value={selectedDepartement}
          onChange={handleDepartementChange}
        >
          {departementOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>



      <div className="filter-dropdown">
        <label htmlFor="rang-filter">Types</label>
        <select
          id="rang-filter"
          value={selectedRang}
          onChange={handleRangChange}
        >
          {rangOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      <div className="filter-dropdown">
        <label htmlFor="nom-filter">Nom entreprise</label>
        <input
          type="text"
          id="nom-filter"
          value={selectedNom}
          onChange={handleNomChange}
          placeholder="Saisir un nom d'entreprise"
        />
      </div>
    </div>
  );

  // Fonction modifiée pour définir les colonnes des acheteurs
  const getAcheteursColumns = () => {
    // Departement affiche depuis le code postal (DOM 3 chiffres, Corse 201/202, sinon 2) : util partage.
    const formatCodePostal = (codePostal: string | undefined) => departementFromCodePostal(codePostal) || '-';

    return [
      {
        field: 'nom',
        header: 'Nom',
        renderCell: (_: any, acheteur: any) => (
          <div className="acheteur-nom-cell">
            {(acheteur.nom || acheteur.organisme?.nom || '-').toUpperCase()}
          </div>
        )
      },
      {
        field: 'localisation',
        header: 'Localisation',
        renderCell: (_: any, acheteur: any) => {
          const formatCodePostal = (codePostal: string | undefined) => departementFromCodePostal(codePostal) || '-';

          // Liste restreinte des pays européens avec codes ISO
          const europeanCountries: Record<string, string> = {
            'Allemagne': 'de',
            'Autriche': 'at',
            'Belgique': 'be',
            'Bulgarie': 'bg',
            'Chypre': 'cy',
            'Croatie': 'hr',
            'Danemark': 'dk',
            'Espagne': 'es',
            'Estonie': 'ee',
            'Finlande': 'fi',
            'France': 'fr',
            'Grèce': 'gr',
            'Hongrie': 'hu',
            'Irlande': 'ie',
            'Italie': 'it',
            'Lettonie': 'lv',
            'Lituanie': 'lt',
            'Luxembourg': 'lu',
            'Malte': 'mt',
            'Pays-Bas': 'nl',
            'Pologne': 'pl',
            'Portugal': 'pt',
            'République tchèque': 'cz',
            'Roumanie': 'ro',
            'Slovaquie': 'sk',
            'Slovénie': 'si',
            'Suède': 'se',
            'Monaco': 'mc',
          };

          const countryName = acheteur.adresse?.pays?.trim() || '';
          const countryCode = europeanCountries[countryName] || null;

          return (
            <div className="acheteur-localisation-cell" style={{ display: 'flex', alignItems: 'center' }}>
              <span className="code-postal">
                {getDepartementLabel(acheteur.adresse?.departement) ||
                  `${formatCodePostal(acheteur?.adresse?.code_postal)} - ${acheteur?.adresse?.departement}`}
              </span>

              <FlagCell country={countryName} code={countryCode || undefined} />
            </div>
          );
        },
      }
    ];
  };

  // Rendu des acheteurs
  const renderAcheteurs = () => {
    const toolbar = (
      <div className="annuaire-filters">
        <div className="filter-dropdown">
          <label htmlFor="ac-categorie-filter">Catégorie</label>
          <select
            id="ac-categorie-filter"
            value={acheteurFilterCategorie}
            onChange={e => setAcheteurFilterCategorie(e.target.value)}
          >
            <option value="">Toutes les catégories</option>
            {acheteurCategorieOptions.map(opt => (
              <option key={opt.val} value={opt.val}>{opt.label}</option>
            ))}
          </select>
        </div>
        <div className="filter-dropdown">
          <label htmlFor="ac-dept-filter">Département</label>
          <select
            id="ac-dept-filter"
            value={acheteurFilterDept}
            onChange={e => setAcheteurFilterDept(e.target.value)}
          >
            <option value="">Tous les départements</option>
            {acheteurDeptOptions.map(code => (
              <option key={code} value={code}>
                {code} – {DEPT_NAMES[code.toUpperCase()] || DEPT_NAMES[code] || code}
              </option>
            ))}
          </select>
        </div>
        <div className="filter-dropdown">
          <label htmlFor="ac-type-filter">Type</label>
          <select
            id="ac-type-filter"
            value={acheteurFilterType}
            onChange={e => setAcheteurFilterType(e.target.value as '' | 'membre' | 'standard')}
          >
            <option value="">Tous les types</option>
            <option value="membre">Acheteur membre</option>
            <option value="standard">Acheteur</option>
          </select>
        </div>
        <div className="filter-dropdown">
          <label htmlFor="ac-nom-filter">Recherche</label>
          <input
            type="text"
            id="ac-nom-filter"
            value={acheteurFilterNom}
            onChange={e => setAcheteurFilterNom(e.target.value)}
            placeholder="Nom de l'organisme..."
          />
        </div>
      </div>
    );

    if (loading) {
      return (
        <>
          {toolbar}
          <section className="annuaire-cards-section">
            <div className="annuaire-cards-grid">
              {Array.from({ length: 8 }).map((_, i) => <PrestataireCardSkeleton key={i} />)}
            </div>
          </section>
        </>
      );
    }

    if (error) {
      return <><div className="error">{error}</div></>;
    }

    return (
      <>
        {toolbar}
        <section className="annuaire-cards-section">
          {filteredAcheteurs.length === 0 ? (
            <p className="acheteurs-empty">Aucun organisme trouvé.</p>
          ) : (
            <div className="annuaire-cards-grid">
              {filteredAcheteurs.map(acheteur => (
                <div
                  key={acheteur.id}
                  className="prestataire-card"
                  onClick={() => { setSelectedAcheteur(acheteur); setShowAcheteurModal(true); }}
                >
                  <div className="prestataire-card-inner">
                    <span className={`prestataire-card-type${acheteur.has_recent_orders ? '' : ' prestataire-card-type--libre'}`}>
                      {acheteur.has_recent_orders ? 'Acheteur membre' : 'Acheteur'}
                    </span>
                    <div className="prestataire-card-header">
                      <div className="prestataire-card-banner-placeholder" style={{ backgroundColor: acheteur.has_recent_orders ? '#f97316' : '#d1d5db' }} />
                    </div>
                    <div className="prestataire-card-logo-wrapper">
                      {acheteur.logo_url ? (
                        <img
                          src={acheteur.logo_url}
                          alt={acheteur.nom}
                          className="prestataire-card-logo-img"
                          onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                        />
                      ) : (
                        <span className="prestataire-card-logo-text">LOGO</span>
                      )}
                    </div>
                    <div className="prestataire-card-body">
                      <p className="prestataire-card-nom" title={acheteur.nom.toUpperCase()}>
                        {acheteur.nom.toUpperCase()}
                      </p>
                      <p className="prestataire-card-lot">
                        {acheteur.departement_code
                          ? formatDepartement(acheteur.departement_code, acheteur.pays)
                          : (acheteur.pays || '—')}
                      </p>
                      <span className="prestataire-card-categorie">
                        {acheteur.type_organisme || 'Non renseigné'}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
        <AcheteurModal
          isOpen={showAcheteurModal}
          onClose={() => { setShowAcheteurModal(false); setSelectedAcheteur(null); }}
          acheteur={selectedAcheteur}
        />
      </>
    );
  };

  return (
    <div className="annuaire-page">
      {!hideTitle && <h1 className="page-title">Annuaire</h1>}
      <div className="annuaire-content">
        {!hideTabs && (
          <TabNav
            tabs={tabs}
            activeTab={activeTab}
            onTabChange={handleTabChange}
          />
        )}

        {isAcheteurTab() ? (
          // Afficher la liste des acheteurs
          renderAcheteurs()
        ) : (
          <>
            {/* Afficher les filtres et le tableau pour l'annuaire des prestataires */}
            {renderFilterDropdowns()}
            {loading ? (
              CARD_VIEW ? (
                <section className="annuaire-cards-section">
                  <div className="annuaire-cards-grid">
                    {Array.from({ length: 8 }).map((_, i) => <PrestataireCardSkeleton key={i} />)}
                  </div>
                </section>
              ) : (
                <div className="loading-indicator">
                  <div className="spinner"></div>
                  <p>Chargement des contacts...</p>
                </div>
              )
            ) : error ? (
              <div className="error">{error}</div>
            ) : (
              CARD_VIEW ? <></> : (
                <TableauDeBord
                  data={contacts}
                  columns={getColumns()}
                  searchPlaceholder="Trouver un Contact"
                  typeFilters={false}
                  onSearch={handleSearch}
                  onExport={handleExport}
                  onFilter={handleFilter}
                  isSelectable={true}
                  // onRowClick={handleRowClick}
                  showActionsColumn={false}
                  showPagination={false}
                />
              )
            )}

            {/* Vue cartes */}
            {CARD_VIEW && !loading && !error && contacts.length > 0 && (
              <section className="annuaire-cards-section">
                <div className="annuaire-cards-grid">
                  {contacts.map((contact) => (
                    <div key={contact.id} className={`prestataire-card${contact.entite?.suspendu ? ' prestataire-card--suspendu' : ''}${!canOpenModal ? ' prestataire-card--readonly' : ''}`} onClick={canOpenModal ? () => {
                      const badge = contact.rang === 0 ? 'Libre' : `Titulaire rang ${contact.rang}`;
                      const contactInfo = contact.prestataire ? { nom: contact.prestataire.nom, prenom: contact.prestataire.prenom, email: contact.prestataire.email, telephone: contact.prestataire.telephone, fonction: contact.prestataire.fonction, contratNumero: contact.rang > 0 ? (contact.numero ?? null) : null } : null;
                      if (contact.rang === 1) {
                        setCommandeInitialValues({
                          lotTechnique: contact.lot_technique?.id?.toString() || '',
                          lotGeographique: selectedDepartement?.toString() || '',
                        });
                      }
                      openProfileModal(contact.entite.id, badge, contactInfo, contact.documents);
                    } : (!user ? () => setShowSignupPrompt(true) : undefined)}>
                      {/* Badge suspendu hors du filtre */}
                      {contact.entite?.suspendu && (
                        <span className="prestataire-card-badge-suspendu">
                          <svg width="10" height="12" viewBox="0 0 11 13" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                            <rect x="1" y="5.5" width="9" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.5" />
                            <path d="M3 5.5V3.5a2.5 2.5 0 0 1 5 0v2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                          </svg>
                          Suspendu
                        </span>
                      )}
                      {/* Contenu filtré (type badge inclus) */}
                      <div className="prestataire-card-inner">
                        <span className={`prestataire-card-type${contact.rang === 0 ? ' prestataire-card-type--libre' : ''}`}>
                          {contact.rang === 0 ? 'Libre' : `Titulaire rang ${contact.rang}`}
                        </span>
                        <div className="prestataire-card-header">
                          {contact.lot_technique?.image_url ? (
                            <img
                              src={contact.lot_technique.image_url}
                              alt={contact.lot_technique.nom}
                              className="prestataire-card-banner-img"
                              onError={(e) => { e.currentTarget.style.display = 'none'; }}
                            />
                          ) : null}
                          {(!contact.lot_technique?.image_url) && (
                            <div
                              className="prestataire-card-banner-placeholder"
                              style={{ backgroundColor: contact.rang === 0 ? '#d1d5db' : '#f97316' }}
                            />
                          )}
                        </div>
                        <div className="prestataire-card-logo-wrapper">
                          {contact.entite?.logo_url ? (
                            <img
                              src={contact.entite.logo_url}
                              alt={contact.entite.nom}
                              className="prestataire-card-logo-img"
                              onLoad={e => (e.currentTarget.style.opacity = '1')}
                              style={{ opacity: 0, transition: 'opacity 0.2s ease' }}
                            />
                          ) : (
                            <span className="prestataire-card-logo-text">LOGO</span>
                          )}
                        </div>
                        <div className="prestataire-card-body">
                          <p className="prestataire-card-nom" title={contact.entite?.nom?.toUpperCase()}>
                            {contact.entite?.nom?.toUpperCase() || '-'}
                          </p>
                          <p className="prestataire-card-lot" title={`${contact.lot_technique?.code} – ${contact.lot_technique?.nom}`}>
                            {contact.lot_technique?.code} – {contact.lot_technique?.nom}
                          </p>
                          {contact.entite?.id != null && (avisParEntite[contact.entite.id]?.nombre ?? 0) > 0 && (
                            <StarRating
                              rating={avisParEntite[contact.entite.id]?.moyenne ?? 0}
                              count={avisParEntite[contact.entite.id]?.nombre ?? 0}
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Modal profil prestataire (vue cartes) */}
            {CARD_VIEW && (
              <ModalProfilMembre
                isOpen={showAnnuaireProfileModal}
                onClose={() => { setShowAnnuaireProfileModal(false); setAnnuaireProfile(null); setAnnuaireProfileError(null); setAnnuaireTypeBadge(null); setAnnuaireContactInfo(null); setAnnuaireContactDocuments(undefined); }}
                profile={annuaireProfile}
                isLoading={annuaireProfileLoading}
                error={annuaireProfileError}
                typeBadge={annuaireTypeBadge}
                contactInfo={annuaireContactInfo}
                contactDocuments={annuaireContactDocuments}
                showCompletion={false}
                showPasserCommande={true}
                onPasserCommande={annuaireTypeBadge === 'Titulaire rang 1' ? () => {
                  setShowAnnuaireProfileModal(false);
                  navigate('/operations', { state: { openBdcModal: true, initialValues: commandeInitialValues } });
                } : undefined}
              />
            )}

            {showSignupPrompt && (
              <div className="modal-overlay" onClick={() => setShowSignupPrompt(false)}>
                <div className="annuaire-signup-prompt" onClick={(e) => e.stopPropagation()}>
                  <button className="fiche-modal-close" onClick={() => setShowSignupPrompt(false)}>&#10005;</button>
                  <h2 className="annuaire-signup-prompt-title">Accédez aux fiches prestataires</h2>
                  <p className="annuaire-signup-prompt-text">
                    Créez votre compte acheteur gratuitement pour consulter les profils complets des prestataires référencés sur Centralis.
                  </p>
                  <div className="annuaire-signup-prompt-actions">
                    <Button
                      value="S'inscrire"
                      variant="primary"
                      onClick={() => navigate('/register')}
                      style={{ fontSize: '1.25rem', padding: '0.85rem 2rem' }}
                    />
                    <Button
                      value="Se connecter"
                      variant="secondary"
                      onClick={() => navigate('/login')}
                      style={{ fontSize: '1.25rem', padding: '0.85rem 2rem' }}
                    />
                  </div>
                </div>
              </div>
            )}

            {openDocumentModalId !== null && (
              <div
                className="modal-overlay"
                onClick={() => setOpenDocumentModalId(null)}
              >
                <div
                  className="annuaire-docs-modal"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="annuaire-docs-modal-header">
                    <h3 className="annuaire-docs-modal-title">{modalTitle}</h3>
                    <button className="fiche-modal-close" onClick={() => setOpenDocumentModalId(null)}>&#10005;</button>
                  </div>
                  <div className="annuaire-docs-modal-body">
                    <DocumentsList documents={modalDocuments || []} variant="fullWidth" />
                  </div>
                  <div className="fiche-modal-footer">
                    <Button value="Fermer" size='small' onClick={() => setOpenDocumentModalId(null)} />
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Annuaire;